import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AccordionSummaryProps,
  ButtonBase,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { border, buildDataTestId, hexOpacity, buildDataTestIdAttribute } from '@src/utils';
import { ReactComponent as ArrowRight } from '@src/icons/Arrows/right.svg';
import { diagnosesApiSlice, IDiagnosesItem } from '@src/api';
import { FC, forwardRef, memo, useMemo, useState } from 'react';
import { theme as muiTheme } from '@src/theme';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { Checkbox } from '@components/Checkbox';

export const DiagnosesOption = styled(ButtonBase)(({ theme }) => ({
  padding: '12px',
  flexDirection: 'row',
  justifyContent: 'start',
  alignItems: 'start',
  gap: '12px',
  borderBottom: border,
  '&:hover': {
    backgroundColor: theme.palette.grey['100'],
  },
  '&:active': {
    backgroundColor: hexOpacity(theme.palette.secondary.main, '7%'),
  },
}));

type DiagnosesOptionItemProps = {
  diagnosis: IDiagnosesItem;
  onClick: () => void;
  checked: boolean;
  disabled: boolean;
  dataTestId?: string;
};
export const DiagnosesOptionItem = memo(
  forwardRef<HTMLButtonElement, DiagnosesOptionItemProps>(
    ({ diagnosis, onClick, checked, disabled, dataTestId }, ref) => {
      const { description, name } = diagnosis;
      return (
        <DiagnosesOption
          onClick={onClick}
          ref={ref}
          disabled={disabled}
          {...buildDataTestIdAttribute(dataTestId, 'button-base')}
        >
          <Checkbox
            checked={checked}
            inputProps={{ 'aria-label': 'controlled' }}
            disabled={disabled}
            sx={{ padding: 0 }}
            {...buildDataTestIdAttribute(dataTestId, 'checkbox')}
          />
          <Typography
            color="grey.700"
            fontSize="14px"
            lineHeight="18px"
            fontWeight={500}
            component="span"
            textAlign="start"
          >
            <strong>{name}</strong> {description}
          </Typography>
        </DiagnosesOption>
      );
    },
  ),
);

export const DiagnosisAccordionSummary = styled((props: AccordionSummaryProps & { level: number }) => (
  <AccordionSummary expandIcon={<ArrowRight />} {...props} />
))(({ theme: localTheme, level }) => ({
  flexDirection: 'row-reverse',
  gap: '6px 12px',
  padding: `6px 12px 6px ${6 + (level - 1) * 12}px`,
  cursor: 'pointer',
  alignItems: 'start',
  minHeight: '0',
  overflow: 'visible',
  boxShadow: 'none',

  '& .MuiAccordionSummary-content': {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: 0,
    minHeight: '0',
    height: '100%',
  },
  '&.Mui-expanded': {
    margin: 0,
    maxWidth: 'none',
    minHeight: '0',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    '& svg': {
      margin: 0,
    },
    '&.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
  },

  '&:hover': {
    backgroundColor: localTheme.palette.grey['100'],
  },
  '&:active': {
    backgroundColor: hexOpacity(localTheme.palette.secondary.main, '7%'),
  },
}));

type DiagnosesCategoryProps = {
  diagnosis: IDiagnosesItem;
  setParentDiagnosisEsbId: (val: string) => void;
  lastItemCategoryDiagnosis: any;
  level: number;
  parentDiagnosisEsbId?: Nullable<string>;
  dataTestId?: string;
};

export const DiagnosesCategory: FC<DiagnosesCategoryProps> = memo(
  ({ diagnosis, setParentDiagnosisEsbId, lastItemCategoryDiagnosis, level, parentDiagnosisEsbId, dataTestId = '' }) => {
    const { description, id, name } = diagnosis;

    const selected = id === parentDiagnosisEsbId;
    const [open, setOpen] = useState(false);
    const [allDiagnosesChildrenCategory, setAllDiagnosesChildrenCategory] = useState<IDiagnosesItem[]>([]);

    const [getUnselectableDiagnoses, { isLoading }] = diagnosesApiSlice.useLazyGetUnselectableDiagnosesQuery();

    const onClickDiagnosisChildren = async () => {
      setParentDiagnosisEsbId(id);
      setOpen(true);
      await getUnselectableDiagnoses({
        search: '',
        parentDiagnosisEsbId: id,
        selectable: false,
      }).then(res => {
        const diagnosesCategory = res.data?.data.filter(({ selectable }) => !selectable) as unknown as IDiagnosesItem[];
        if (diagnosesCategory?.length === 0) setOpen(false);
        return setAllDiagnosesChildrenCategory(diagnosesCategory);
      });
    };

    const onChangeAccordion = async (_ev: any, isOpen: boolean) => {
      if (isOpen) {
        await onClickDiagnosisChildren();
      } else {
        setOpen(false);
      }
    };
    const summaryFontStyle = useMemo(() => {
      switch (level) {
        case 1: {
          return { fontWeight: 700 };
        }
        case 2: {
          return { fontWeight: 700 };
        }
        case 3: {
          return { fontWeight: 600 };
        }
        case 4: {
          return { fontWeight: 600 };
        }
        default: {
          return { fontWeight: 500 };
        }
      }
    }, [level]);

    return (
      <Accordion
        key={`${description}/${id}`}
        sx={{
          boxShadow: 'none!important',
          padding: 0,
          overflow: 'visible',
          boxSizing: 'border-box',
          '&:before': {
            backgroundColor: '#fff',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        }}
        ref={lastItemCategoryDiagnosis || null}
        expanded={open}
        onChange={onChangeAccordion}
        {...buildDataTestIdAttribute(dataTestId)}
      >
        <DiagnosisAccordionSummary
          level={level}
          sx={{
            ...(selected ? { backgroundColor: hexOpacity(muiTheme.palette.secondary.main, '7%') } : {}),
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {isLoading && <PreloaderBubbles size={6} />}
          <Stack ml="6px" direction="row" gap="12px">
            <Typography
              color="grey.700"
              sx={{
                flexShrink: 0,
                fontSize: '14px',
                lineHeight: '16px',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                ...summaryFontStyle,
              }}
            >
              {name}
            </Typography>
            <Typography
              color="grey.700"
              sx={{
                fontSize: '14px',
                lineHeight: '16px',
                ...summaryFontStyle,
              }}
            >
              {description}
            </Typography>
          </Stack>
        </DiagnosisAccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {allDiagnosesChildrenCategory?.map((childrenDiagnosis, index) => (
            <DiagnosesCategory
              lastItemCategoryDiagnosis={null}
              key={childrenDiagnosis.id}
              diagnosis={childrenDiagnosis}
              setParentDiagnosisEsbId={setParentDiagnosisEsbId}
              level={level + 1}
              parentDiagnosisEsbId={parentDiagnosisEsbId}
              dataTestId={buildDataTestId(dataTestId, `children-${index}`)}
            />
          ))}
        </AccordionDetails>
      </Accordion>
    );
  },
);

DiagnosesCategory.displayName = 'DiagnosesCategory';
